/**
 * Data layer for `creative-ab`. Thin typed fetch wrappers over
 * /api/v1/creative-ab. Raw `fetch()` is confined to this file per the dash
 * contract. Mirrors BE shape (apps/api/src/routes/creative-ab.ts).
 *
 * Endpoints:
 *   PUT  /campaign/:campaign_id/variants   — atomic replace; sum=10_000 bps
 *   GET  /campaign/:campaign_id/variants   — list current variants
 *   POST /campaign/:campaign_id/assign     — deterministic per click_id
 *   POST /campaign/:campaign_id/convert    — flip converted=1 for click_id
 *   GET  /campaign/:campaign_id/report     — per-variant views/conv/cr_bps
 */

export type AssetKind = "creative" | "lp";

export interface VariantRow {
	id: string;
	tenant_id: string;
	campaign_id: string;
	variant_key: string;
	asset_kind: AssetKind;
	asset_ref: string;
	split_bps: number;
	active: boolean;
	created_at: number;
	updated_at: number;
}

export interface VariantInput {
	variant_key: string;
	asset_kind: AssetKind;
	asset_ref: string;
	split_bps: number;
}

export interface VariantReport {
	variant_key: string;
	views: number;
	conversions: number;
	cr_bps: number;
}

export interface VariantsResponse {
	campaign_id: string;
	variants: VariantRow[];
}

export interface AssignResponse {
	campaign_id: string;
	click_id: string;
	variant_key: string;
	asset_kind: AssetKind;
	asset_ref: string;
	ts: number;
}

export interface ConvertResponse {
	campaign_id: string;
	click_id: string;
	variant_key: string;
}

export interface ReportResponse {
	campaign_id: string;
	variants: VariantReport[];
}

const BASE = "/api/v1/creative-ab";
export const TOTAL_BPS = 10_000;

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function campaignPath(campaignId: string, suffix: string): string {
	return `${BASE}/campaign/${encodeURIComponent(campaignId)}${suffix}`;
}

export async function listVariants(
	campaignId: string,
): Promise<VariantsResponse> {
	const res = await fetch(campaignPath(campaignId, "/variants"), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<VariantsResponse>(res);
}

export async function replaceVariants(
	campaignId: string,
	variants: VariantInput[],
): Promise<VariantsResponse> {
	const res = await fetch(campaignPath(campaignId, "/variants"), {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ variants }),
	});
	return parseOrThrow<VariantsResponse>(res);
}

export async function assign(
	campaignId: string,
	clickId: string,
): Promise<AssignResponse> {
	const res = await fetch(campaignPath(campaignId, "/assign"), {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ click_id: clickId }),
	});
	return parseOrThrow<AssignResponse>(res);
}

export async function convert(
	campaignId: string,
	clickId: string,
): Promise<ConvertResponse> {
	const res = await fetch(campaignPath(campaignId, "/convert"), {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({ click_id: clickId }),
	});
	return parseOrThrow<ConvertResponse>(res);
}

export async function report(campaignId: string): Promise<ReportResponse> {
	const res = await fetch(campaignPath(campaignId, "/report"), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ReportResponse>(res);
}

export const creativeAbQueryKeys = {
	all: ["creative-ab"] as const,
	variants: (campaignId: string) =>
		[...creativeAbQueryKeys.all, "variants", campaignId] as const,
	report: (campaignId: string) =>
		[...creativeAbQueryKeys.all, "report", campaignId] as const,
};
